import React, { useMemo, useState, useEffect } from 'react';

function PerformanceChart({ data, bets, displayMode = 'dollars', unitSize = 1 }) {
    const isUnits = displayMode === 'units';
    const [hoveredPoint, setHoveredPoint] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [chartType, setChartType] = useState('line'); // 'line' or 'candlestick'
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth < 768 : false
    );

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Responsive viewBox: a more square aspect ratio on mobile so the chart isn't
    // squished vertically when scaled to the screen width.
    const chartDimensions = useMemo(() => (
        isMobile
            ? { width: 420, height: 320, padding: { top: 24, right: 16, bottom: 36, left: 44 } }
            : { width: 800, height: 300, padding: { top: 40, right: 60, bottom: 50, left: 70 } }
    ), [isMobile]);

    const axisFontSize = isMobile ? 12 : 10;
    const maxXLabels = isMobile ? 4 : 6;

    // Calculate daily P/L data for candlestick chart
    const dailyData = useMemo(() => {
        if (!bets || bets.length === 0) return [];
        
        const dailyMap = {};
        const sortedBets = [...bets].sort((a, b) => new Date(a.date) - new Date(b.date));
        
        sortedBets.forEach(bet => {
            if (!dailyMap[bet.date]) {
                dailyMap[bet.date] = {
                    date: bet.date,
                    bets: [],
                    open: 0,
                    close: 0,
                    high: 0,
                    low: 0,
                    pl: 0
                };
            }
            dailyMap[bet.date].bets.push(bet);
            dailyMap[bet.date].pl += bet.pl;
        });

        // Calculate cumulative and OHLC values
        let cumulative = 0;
        const dailyArray = Object.values(dailyMap).sort((a, b) => new Date(a.date) - new Date(b.date));
        
        dailyArray.forEach(day => {
            day.open = cumulative;
            
            // Simulate intraday movement based on individual bets
            let runningPL = cumulative;
            let high = cumulative;
            let low = cumulative;
            
            day.bets.forEach(bet => {
                runningPL += bet.pl;
                high = Math.max(high, runningPL);
                low = Math.min(low, runningPL);
            });
            
            day.close = cumulative + day.pl;
            day.high = high;
            day.low = low;
            cumulative = day.close;
        });

        return dailyArray;
    }, [bets]);

    const { pathData, areaPath, points, yAxisLabels, xAxisLabels, minValue, maxValue, zeroY } = useMemo(() => {
        if (data.length === 0) {
            return { pathData: '', areaPath: '', points: [], yAxisLabels: [], xAxisLabels: [], minValue: 0, maxValue: 0, zeroY: 0 };
        }

        const { width, height, padding } = chartDimensions;
        const chartWidth = width - padding.left - padding.right;
        const chartHeight = height - padding.top - padding.bottom;

        // Helper function to get nice round numbers for axis
        const getNiceNumber = (value, round) => {
            const exponent = Math.floor(Math.log10(Math.abs(value) || 1));
            const fraction = value / Math.pow(10, exponent);
            let niceFraction;
            
            if (round) {
                if (fraction < 1.5) niceFraction = 1;
                else if (fraction < 3) niceFraction = 2;
                else if (fraction < 7) niceFraction = 5;
                else niceFraction = 10;
            } else {
                if (fraction <= 1) niceFraction = 1;
                else if (fraction <= 2) niceFraction = 2;
                else if (fraction <= 5) niceFraction = 5;
                else niceFraction = 10;
            }
            
            return niceFraction * Math.pow(10, exponent);
        };

        // Calculate min/max with nice rounding
        const values = data.map(d => d.cumulative);
        const rawMin = Math.min(0, ...values);
        const rawMax = Math.max(0, ...values);
        const valueRange = rawMax - rawMin || 100;
        
        // Calculate nice tick spacing
        const tickSpacing = getNiceNumber(valueRange / 5, true);
        
        // Round min/max to nice values
        const minVal = Math.floor(rawMin / tickSpacing) * tickSpacing;
        let maxVal = Math.ceil(rawMax / tickSpacing) * tickSpacing;
        // Guard against a zero-height range (e.g. all values equal / all pending)
        // which would otherwise produce NaN coordinates and render nothing.
        if (maxVal === minVal) maxVal = minVal + tickSpacing;

        // Calculate points
        const pts = data.map((d, i) => {
            const x = padding.left + (i / (data.length - 1 || 1)) * chartWidth;
            const y = padding.top + chartHeight - ((d.cumulative - minVal) / (maxVal - minVal)) * chartHeight;
            return { x, y, ...d };
        });

        // Generate smooth curve path using Catmull-Rom spline
        const generateSmoothPath = (points) => {
            if (points.length < 2) return '';
            
            const tension = 0.3;
            let path = `M ${points[0].x} ${points[0].y}`;
            
            for (let i = 0; i < points.length - 1; i++) {
                const p0 = points[Math.max(0, i - 1)];
                const p1 = points[i];
                const p2 = points[i + 1];
                const p3 = points[Math.min(points.length - 1, i + 2)];
                
                const cp1x = p1.x + (p2.x - p0.x) * tension;
                const cp1y = p1.y + (p2.y - p0.y) * tension;
                const cp2x = p2.x - (p3.x - p1.x) * tension;
                const cp2y = p2.y - (p3.y - p1.y) * tension;
                
                path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
            }
            
            return path;
        };

        const linePath = generateSmoothPath(pts);
        
        // Area path (fill under the curve to zero line)
        const zeroLineY = padding.top + chartHeight - ((0 - minVal) / (maxVal - minVal)) * chartHeight;
        const area = `${linePath} L ${pts[pts.length - 1].x} ${zeroLineY} L ${pts[0].x} ${zeroLineY} Z`;

        // Generate Y-axis labels with nice round numbers (keep raw value so
        // fractional unit ticks aren't collapsed; formatAxisValue handles display)
        const yLabels = [];
        for (let value = maxVal; value >= minVal; value -= tickSpacing) {
            const y = padding.top + chartHeight - ((value - minVal) / (maxVal - minVal)) * chartHeight;
            yLabels.push({ value, y });
        }

        // Generate X-axis labels (dates)
        const xLabels = [];
        const numXLabels = Math.min(maxXLabels, data.length);
        for (let i = 0; i < numXLabels; i++) {
            const index = Math.floor((i / (numXLabels - 1 || 1)) * (data.length - 1));
            const x = pts[index]?.x || 0;
            const date = data[index]?.date || '';
            xLabels.push({ date, x });
        }

        return {
            pathData: linePath,
            areaPath: area,
            points: pts,
            yAxisLabels: yLabels,
            xAxisLabels: xLabels,
            minValue: minVal,
            maxValue: maxVal,
            zeroY: zeroLineY
        };
    }, [data, chartDimensions, maxXLabels]);

    // Candlestick chart calculations
    const candlestickData = useMemo(() => {
        if (dailyData.length === 0) {
            return { candles: [], yAxisLabels: [], xAxisLabels: [], minValue: 0, maxValue: 0, zeroY: 0, tickSpacing: 1000 };
        }

        const { width, height, padding } = chartDimensions;
        const chartWidth = width - padding.left - padding.right;
        const chartHeight = height - padding.top - padding.bottom;

        // Helper function to get nice round numbers for axis
        const getNiceNumber = (value, round) => {
            const exponent = Math.floor(Math.log10(Math.abs(value) || 1));
            const fraction = value / Math.pow(10, exponent);
            let niceFraction;
            
            if (round) {
                if (fraction < 1.5) niceFraction = 1;
                else if (fraction < 3) niceFraction = 2;
                else if (fraction < 7) niceFraction = 5;
                else niceFraction = 10;
            } else {
                if (fraction <= 1) niceFraction = 1;
                else if (fraction <= 2) niceFraction = 2;
                else if (fraction <= 5) niceFraction = 5;
                else niceFraction = 10;
            }
            
            return niceFraction * Math.pow(10, exponent);
        };

        // Calculate min/max with nice rounding
        const allValues = dailyData.flatMap(d => [d.high, d.low, d.open, d.close]);
        const rawMin = Math.min(0, ...allValues);
        const rawMax = Math.max(0, ...allValues);
        const valueRange = rawMax - rawMin || 100;
        
        // Calculate nice tick spacing
        const tickSpacing = getNiceNumber(valueRange / 5, true);
        
        // Round min/max to nice values
        const minVal = Math.floor(rawMin / tickSpacing) * tickSpacing;
        let maxVal = Math.ceil(rawMax / tickSpacing) * tickSpacing;
        if (maxVal === minVal) maxVal = minVal + tickSpacing;

        const candleWidth = Math.max(8, Math.min(30, (chartWidth / dailyData.length) * 0.6));
        const gap = (chartWidth - (candleWidth * dailyData.length)) / (dailyData.length + 1);

        const candles = dailyData.map((d, i) => {
            const x = padding.left + gap + i * (candleWidth + gap) + candleWidth / 2;
            const scaleY = (val) => padding.top + chartHeight - ((val - minVal) / (maxVal - minVal)) * chartHeight;
            
            return {
                ...d,
                x,
                candleWidth,
                openY: scaleY(d.open),
                closeY: scaleY(d.close),
                highY: scaleY(d.high),
                lowY: scaleY(d.low),
                isGreen: d.close >= d.open
            };
        });

        const zeroLineY = padding.top + chartHeight - ((0 - minVal) / (maxVal - minVal)) * chartHeight;

        // Y-axis labels with nice round numbers (raw value; display handled by formatAxisValue)
        const yLabels = [];
        for (let value = maxVal; value >= minVal; value -= tickSpacing) {
            const y = padding.top + chartHeight - ((value - minVal) / (maxVal - minVal)) * chartHeight;
            yLabels.push({ value, y });
        }

        // X-axis labels
        const xLabels = [];
        const numXLabels = Math.min(maxXLabels, dailyData.length);
        for (let i = 0; i < numXLabels; i++) {
            const index = Math.floor((i / (numXLabels - 1 || 1)) * (dailyData.length - 1));
            const candle = candles[index];
            if (candle) {
                xLabels.push({ date: candle.date, x: candle.x });
            }
        }

        return {
            candles,
            yAxisLabels: yLabels,
            xAxisLabels: xLabels,
            minValue: minVal,
            maxValue: maxVal,
            zeroY: zeroLineY
        };
    }, [dailyData, chartDimensions, maxXLabels]);

    const handleMouseMove = (e, point) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const clientX = e.touches?.[0]?.clientX ?? e.clientX;
        const clientY = e.touches?.[0]?.clientY ?? e.clientY;
        setTooltipPosition({ x: clientX - rect.left, y: clientY - rect.top });
        setHoveredPoint(point);
    };

    // Find closest point to a clientX position relative to a chart-area rect
    const handleChartPointerMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const clientX = e.touches?.[0]?.clientX ?? e.clientX;
        const clientY = e.touches?.[0]?.clientY ?? e.clientY;
        const mouseX = clientX - rect.left;
        const chartWidth = chartDimensions.width - chartDimensions.padding.left - chartDimensions.padding.right;
        const relativeX = mouseX / rect.width * chartWidth;

        let closestPoint = points[0];
        let minDist = Infinity;
        points.forEach(point => {
            const pointRelativeX = point.x - chartDimensions.padding.left;
            const dist = Math.abs(pointRelativeX - relativeX);
            if (dist < minDist) {
                minDist = dist;
                closestPoint = point;
            }
        });

        if (closestPoint) {
            setTooltipPosition({ x: clientX - rect.left, y: clientY - rect.top });
            setHoveredPoint(closestPoint);
        }
    };

    const formatCurrency = (value) => {
        if (isUnits) {
            const units = value / unitSize;
            const sign = units >= 0 ? '+' : '-';
            const n = (Math.round((Math.abs(units) + Number.EPSILON) * 100) / 100).toFixed(2).replace(/\.?0+$/, '');
            return `${sign}${n}U`;
        }
        const prefix = value >= 0 ? '+' : '';
        return `${prefix}$${Math.abs(value).toFixed(2)}`;
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        if (isMobile) {
            // Compact "10/3" form on mobile to save horizontal space
            return `${date.getMonth() + 1}/${date.getDate()}`;
        }
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Compact y-axis label (e.g. $1.2k, -$340) — keeps mobile axis narrow
    const formatAxisValue = (value) => {
        const abs = Math.abs(value);
        const sign = value < 0 ? '-' : '';
        if (isUnits) {
            const units = abs / unitSize;
            const n = units >= 100 ? Math.round(units).toString() : units.toFixed(1).replace(/\.0$/, '');
            return `${sign}${n}U`;
        }
        if (isMobile) {
            if (abs >= 10000) return `${sign}$${(abs / 1000).toFixed(0)}k`;
            if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
            return `${sign}$${Math.round(abs)}`;
        }
        return `${sign}$${Math.round(abs)}`;
    };

    const lastValue = data.length > 0 ? data[data.length - 1].cumulative : 0;
    const isPositive = lastValue >= 0;

    return (
        <div className="bg-[#0f172a] rounded border border-slate-700/50 overflow-hidden">
            {/* Command Bar Header */}
            <div className="px-3 md:px-4 py-2.5 border-b border-slate-700/50 bg-slate-800/30">
                {/* Top row: title + total P/L (always inline) */}
                <div className="flex items-center justify-between gap-2 min-w-0">
                    <div className="flex items-center gap-2 md:gap-3 min-w-0">
                        <div className={`w-1 h-4 flex-shrink-0 ${isPositive ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        <h3 className="text-xs md:text-sm font-semibold text-white tracking-tight whitespace-nowrap">P/L Performance</h3>
                        <span className="hidden md:inline text-[10px] font-mono text-slate-600 tracking-widest uppercase">Cumulative</span>
                    </div>
                    <div className="flex items-center gap-2 md:gap-3">
                        <div className={`text-base md:text-lg font-bold font-mono tabular-nums whitespace-nowrap ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatCurrency(lastValue)}
                        </div>
                        {/* Toggle: visible inline on >=md */}
                        <div className="hidden md:block w-px h-5 bg-slate-700/50" />
                        <div className="hidden md:flex bg-slate-800/50 border border-slate-700/50 rounded p-0.5">
                            <button
                                onClick={() => setChartType('line')}
                                className={`px-2 py-1 rounded text-[10px] font-mono font-medium transition-all flex items-center gap-1 ${
                                    chartType === 'line' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-white'
                                }`}
                                title="Line chart"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 17l6-6 4 4 8-8" />
                                </svg>
                                <span>LINE</span>
                            </button>
                            <button
                                onClick={() => setChartType('candlestick')}
                                className={`px-2 py-1 rounded text-[10px] font-mono font-medium transition-all flex items-center gap-1 ${
                                    chartType === 'candlestick' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-white'
                                }`}
                                title="Candlestick chart"
                            >
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <line x1="7" y1="3" x2="7" y2="21" strokeWidth="1.5" />
                                    <line x1="17" y1="3" x2="17" y2="21" strokeWidth="1.5" />
                                    <rect x="5" y="7" width="4" height="6" strokeWidth="1.5" fill="currentColor" opacity="0.4" />
                                    <rect x="15" y="11" width="4" height="6" strokeWidth="1.5" fill="currentColor" opacity="0.4" />
                                </svg>
                                <span>OHLC</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile row: chart type toggle full-width below header */}
                <div className="md:hidden mt-2 flex items-center justify-between gap-2">
                    <span className="text-[9px] font-mono text-slate-600 tracking-widest uppercase">Cumulative</span>
                    <div className="flex bg-slate-800/50 border border-slate-700/50 rounded p-0.5">
                        <button
                            onClick={() => setChartType('line')}
                            className={`px-2.5 py-1 rounded text-[10px] font-mono font-medium transition-all flex items-center gap-1 ${
                                chartType === 'line' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500'
                            }`}
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 17l6-6 4 4 8-8" />
                            </svg>
                            <span>LINE</span>
                        </button>
                        <button
                            onClick={() => setChartType('candlestick')}
                            className={`px-2.5 py-1 rounded text-[10px] font-mono font-medium transition-all flex items-center gap-1 ${
                                chartType === 'candlestick' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500'
                            }`}
                        >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <line x1="7" y1="3" x2="7" y2="21" strokeWidth="1.5" />
                                <line x1="17" y1="3" x2="17" y2="21" strokeWidth="1.5" />
                                <rect x="5" y="7" width="4" height="6" strokeWidth="1.5" fill="currentColor" opacity="0.4" />
                                <rect x="15" y="11" width="4" height="6" strokeWidth="1.5" fill="currentColor" opacity="0.4" />
                            </svg>
                            <span>OHLC</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Chart - extends to card edges on mobile, capped on ultrawide screens */}
            <div className="relative w-auto px-1 py-1 md:p-4 2xl:max-w-[92%] 2xl:mx-auto">
                <svg 
                    viewBox={`0 0 ${chartDimensions.width} ${chartDimensions.height}`}
                    className="w-full h-auto"
                >
                    <defs>
                        {/* Gradient for positive area */}
                        <linearGradient id="positiveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
                            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                        </linearGradient>
                        
                        {/* Gradient for negative area */}
                        <linearGradient id="negativeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#ef4444" stopOpacity="0" />
                            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.3" />
                        </linearGradient>

                        {/* Line gradient */}
                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#10b981" />
                            <stop offset="100%" stopColor="#14b8a6" />
                        </linearGradient>

                        {/* Glow filter */}
                        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>

                        {/* Candlestick glow */}
                        <filter id="candleGlow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    {chartType === 'line' ? (
                        <>
                            {/* Grid lines */}
                            {yAxisLabels.map((label, i) => (
                                <line
                                    key={i}
                                    x1={chartDimensions.padding.left}
                                    y1={label.y}
                                    x2={chartDimensions.width - chartDimensions.padding.right}
                                    y2={label.y}
                                    stroke="#334155"
                                    strokeWidth="1"
                                    strokeDasharray={Math.abs(label.value) < 1e-9 ? "none" : "4,4"}
                                    opacity={Math.abs(label.value) < 1e-9 ? 0.8 : 0.3}
                                />
                            ))}

                            {/* Zero line highlight */}
                            <line
                                x1={chartDimensions.padding.left}
                                y1={zeroY}
                                x2={chartDimensions.width - chartDimensions.padding.right}
                                y2={zeroY}
                                stroke="#64748b"
                                strokeWidth="1"
                            />

                            {/* Area fill */}
                            <path
                                d={areaPath}
                                fill={isPositive ? "url(#positiveGradient)" : "url(#negativeGradient)"}
                                opacity="0.5"
                            />

                            {/* Main line */}
                            <path
                                d={pathData}
                                fill="none"
                                stroke={isPositive ? "url(#lineGradient)" : "#ef4444"}
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                filter="url(#glow)"
                            />

                            {/* Single data point has no line to draw, so show a marker */}
                            {points.length === 1 && (
                                <circle
                                    cx={points[0].x}
                                    cy={points[0].y}
                                    r="5"
                                    fill={isPositive ? "#10b981" : "#ef4444"}
                                    stroke="#020817"
                                    strokeWidth="2"
                                    filter="url(#glow)"
                                />
                            )}

                            {/* Y-axis labels */}
                            {yAxisLabels.map((label, i) => (
                                <text
                                    key={i}
                                    x={chartDimensions.padding.left - (isMobile ? 6 : 12)}
                                    y={label.y + 4}
                                    fill="#475569"
                                    fontSize={axisFontSize}
                                    textAnchor="end"
                                    fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
                                >
                                    {formatAxisValue(label.value)}
                                </text>
                            ))}

                            {/* X-axis labels */}
                            {xAxisLabels.map((label, i) => (
                                <text
                                    key={i}
                                    x={label.x}
                                    y={chartDimensions.height - chartDimensions.padding.bottom + (isMobile ? 18 : 25)}
                                    fill="#475569"
                                    fontSize={axisFontSize}
                                    textAnchor="middle"
                                    fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
                                >
                                    {formatDate(label.date)}
                                </text>
                            ))}

                            {/* Invisible hover area for the entire chart (also handles touch) */}
                            <rect
                                x={chartDimensions.padding.left}
                                y={chartDimensions.padding.top}
                                width={chartDimensions.width - chartDimensions.padding.left - chartDimensions.padding.right}
                                height={chartDimensions.height - chartDimensions.padding.top - chartDimensions.padding.bottom}
                                fill="transparent"
                                onMouseMove={handleChartPointerMove}
                                onMouseLeave={() => setHoveredPoint(null)}
                                onTouchStart={handleChartPointerMove}
                                onTouchMove={(e) => { e.preventDefault(); handleChartPointerMove(e); }}
                                onTouchEnd={() => setHoveredPoint(null)}
                                style={{ cursor: 'crosshair', touchAction: 'none' }}
                            />
                            
                            {/* Hover indicator line and point */}
                            {hoveredPoint && (
                                <>
                                    {/* Vertical line */}
                                    <line
                                        x1={hoveredPoint.x}
                                        y1={chartDimensions.padding.top}
                                        x2={hoveredPoint.x}
                                        y2={chartDimensions.height - chartDimensions.padding.bottom}
                                        stroke="#64748b"
                                        strokeWidth="1"
                                        strokeDasharray="4,4"
                                        opacity="0.5"
                                    />
                                    {/* Point indicator */}
                                    <circle
                                        cx={hoveredPoint.x}
                                        cy={hoveredPoint.y}
                                        r="6"
                                        fill={hoveredPoint.cumulative >= 0 ? "#10b981" : "#ef4444"}
                                        stroke="#020817"
                                        strokeWidth="2"
                                        style={{ 
                                            filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.5))'
                                        }}
                                    />
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {/* Candlestick Chart */}
                            {/* Grid lines */}
                            {candlestickData.yAxisLabels.map((label, i) => (
                                <line
                                    key={i}
                                    x1={chartDimensions.padding.left}
                                    y1={label.y}
                                    x2={chartDimensions.width - chartDimensions.padding.right}
                                    y2={label.y}
                                    stroke="#334155"
                                    strokeWidth="1"
                                    strokeDasharray={Math.abs(label.value) < 0.01 ? "none" : "4,4"}
                                    opacity={Math.abs(label.value) < 0.01 ? 0.8 : 0.3}
                                />
                            ))}

                            {/* Zero line */}
                            <line
                                x1={chartDimensions.padding.left}
                                y1={candlestickData.zeroY}
                                x2={chartDimensions.width - chartDimensions.padding.right}
                                y2={candlestickData.zeroY}
                                stroke="#64748b"
                                strokeWidth="1"
                            />

                            {/* Candlesticks */}
                            {candlestickData.candles.map((candle, i) => {
                                const isHovered = hoveredPoint?.date === candle.date;
                                const bodyTop = Math.min(candle.openY, candle.closeY);
                                const bodyHeight = Math.abs(candle.closeY - candle.openY) || 2;
                                
                                return (
                                    <g 
                                        key={i}
                                        onMouseEnter={(e) => handleMouseMove(e, candle)}
                                        onMouseMove={(e) => handleMouseMove(e, candle)}
                                        onMouseLeave={() => setHoveredPoint(null)}
                                        onTouchStart={(e) => handleMouseMove(e, candle)}
                                        onTouchMove={(e) => { e.preventDefault(); handleMouseMove(e, candle); }}
                                        onTouchEnd={() => setHoveredPoint(null)}
                                        style={{ cursor: 'pointer', touchAction: 'none' }}
                                    >
                                        {/* Wick (high-low line) */}
                                        <line
                                            x1={candle.x}
                                            y1={candle.highY}
                                            x2={candle.x}
                                            y2={candle.lowY}
                                            stroke={candle.isGreen ? "#10b981" : "#ef4444"}
                                            strokeWidth={isHovered ? 2 : 1.5}
                                            filter={isHovered ? "url(#candleGlow)" : "none"}
                                        />
                                        
                                        {/* Body */}
                                        <rect
                                            x={candle.x - candle.candleWidth / 2}
                                            y={bodyTop}
                                            width={candle.candleWidth}
                                            height={bodyHeight}
                                            fill={candle.isGreen ? "#10b981" : "#ef4444"}
                                            stroke={candle.isGreen ? "#059669" : "#dc2626"}
                                            strokeWidth={isHovered ? 2 : 1}
                                            rx="2"
                                            filter={isHovered ? "url(#candleGlow)" : "none"}
                                            className="transition-all duration-150"
                                        />
                                        
                                        {/* Gradient overlay for depth */}
                                        <rect
                                            x={candle.x - candle.candleWidth / 2}
                                            y={bodyTop}
                                            width={candle.candleWidth}
                                            height={bodyHeight}
                                            fill="url(#candleShine)"
                                            rx="2"
                                            opacity="0.3"
                                        />
                                    </g>
                                );
                            })}

                            {/* Y-axis labels */}
                            {candlestickData.yAxisLabels.map((label, i) => (
                                <text
                                    key={i}
                                    x={chartDimensions.padding.left - (isMobile ? 6 : 12)}
                                    y={label.y + 4}
                                    fill="#475569"
                                    fontSize={axisFontSize}
                                    textAnchor="end"
                                    fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
                                >
                                    {formatAxisValue(label.value)}
                                </text>
                            ))}

                            {/* X-axis labels */}
                            {candlestickData.xAxisLabels.map((label, i) => (
                                <text
                                    key={i}
                                    x={label.x}
                                    y={chartDimensions.height - chartDimensions.padding.bottom + (isMobile ? 18 : 25)}
                                    fill="#475569"
                                    fontSize={axisFontSize}
                                    textAnchor="middle"
                                    fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
                                >
                                    {formatDate(label.date)}
                                </text>
                            ))}
                        </>
                    )}
                </svg>

                {/* Tooltip */}
                {hoveredPoint && (
                    <div
                        className="absolute pointer-events-none bg-slate-900 border border-slate-700/70 rounded shadow-2xl z-10 min-w-[180px]"
                        style={{
                            left: Math.min(tooltipPosition.x + 10, chartDimensions.width - 200),
                            top: tooltipPosition.y - 80,
                            transform: 'translateX(-50%)'
                        }}
                    >
                        <div className="px-2.5 py-1.5 border-b border-slate-700/50 bg-slate-800/40">
                            <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase">{formatDate(hoveredPoint.date)}</div>
                        </div>
                        <div className="px-2.5 py-2">
                            {chartType === 'line' ? (
                                <>
                                    <div className="text-[11px] font-mono text-white font-medium mb-0.5 truncate max-w-[180px]">
                                        {hoveredPoint.game}
                                    </div>
                                    <div className="text-[10px] font-mono text-slate-400 mb-2 truncate max-w-[180px]">
                                        {hoveredPoint.bet}
                                    </div>
                                    <div className="flex items-center gap-3 pt-1.5 border-t border-slate-700/50">
                                        <div>
                                            <div className="text-[9px] font-mono text-slate-600 tracking-widest uppercase">Bet</div>
                                            <div className={`text-[11px] font-mono font-semibold tabular-nums ${hoveredPoint.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {formatCurrency(hoveredPoint.pl)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[9px] font-mono text-slate-600 tracking-widest uppercase">Cumul</div>
                                            <div className={`text-[11px] font-mono font-semibold tabular-nums ${hoveredPoint.cumulative >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {formatCurrency(hoveredPoint.cumulative)}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="text-[11px] font-mono text-white font-medium mb-2">
                                        {hoveredPoint.bets?.length || 0} bet{hoveredPoint.bets?.length !== 1 ? 's' : ''}
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono">
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">O</span>
                                            <span className="text-slate-300 tabular-nums">{formatCurrency(hoveredPoint.open)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">H</span>
                                            <span className="text-emerald-400 tabular-nums">{formatCurrency(hoveredPoint.high)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">C</span>
                                            <span className={`tabular-nums ${hoveredPoint.close >= hoveredPoint.open ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {formatCurrency(hoveredPoint.close)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">L</span>
                                            <span className="text-red-400 tabular-nums">{formatCurrency(hoveredPoint.low)}</span>
                                        </div>
                                    </div>
                                    <div className="mt-2 pt-1.5 border-t border-slate-700/50 flex items-center justify-between">
                                        <span className="text-[9px] font-mono text-slate-600 tracking-widest uppercase">Day P/L</span>
                                        <span className={`text-[11px] font-mono font-semibold tabular-nums ${hoveredPoint.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {formatCurrency(hoveredPoint.pl)}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PerformanceChart;
